export interface BrandColors {
  primary_color?: string;
  accent_color?: string;
  success_color?: string;
}

export function applyTheme(colors: BrandColors): void {
  const root = document.documentElement;
  if (colors.primary_color) {
    root.style.setProperty('--color-brand-dark', hexToChannels(colors.primary_color));
    // Darkened variant for dark-mode surfaces (70% shade over black) so bg-brand-dark
    // elements don't overpower the dark background at full brightness.
    root.style.setProperty('--color-brand-dark-dm', shadeChannels(colors.primary_color, 0.70));
    // brand-light is a light tint of the primary (15% primary over white),
    // matching the original #ECD6FF lavender used for chips / icon backgrounds.
    root.style.setProperty('--color-brand-light', tintChannels(colors.primary_color, 0.15));
    // AI Chat dark-mode surfaces: brand-tinted deep shades. Light mode uses the white
    // values hardcoded in :root (index.css) — no light variant needed here.
    root.style.setProperty('--color-chat-bg-dark',       shadeChannels(colors.primary_color, 0.17));
    root.style.setProperty('--color-chat-surface-dark',  shadeChannels(colors.primary_color, 0.25));
    root.style.setProperty('--color-chat-raised-dark',   shadeChannels(colors.primary_color, 0.30));

    // ── Semantic UI surface tokens — brand-tinted ──────────────────────────
    // Light mode: extremely subtle tint over white so the page feels on-brand
    // without looking coloured. 2–5% primary over white.
    root.style.setProperty('--ui-bg-light',      tintChannels(colors.primary_color, 0.02));
    root.style.setProperty('--ui-card-light',     tintChannels(colors.primary_color, 0.00)); // pure white
    root.style.setProperty('--ui-raised-light',   tintChannels(colors.primary_color, 0.03));
    root.style.setProperty('--ui-subtle-light',   tintChannels(colors.primary_color, 0.05));
    root.style.setProperty('--ui-input-light',    tintChannels(colors.primary_color, 0.00)); // pure white
    root.style.setProperty('--ui-hover-light',    tintChannels(colors.primary_color, 0.06));
    root.style.setProperty('--ui-border-light',   tintChannels(colors.primary_color, 0.12));
    root.style.setProperty('--ui-border2-light',  tintChannels(colors.primary_color, 0.06));
    root.style.setProperty('--ui-border3-light',  tintChannels(colors.primary_color, 0.22));

    // Dark mode: progressively lighter dark shades of the primary color so the
    // whole app carries the brand hue instead of generic gray/slate.
    root.style.setProperty('--ui-bg-dark',        shadeChannels(colors.primary_color, 0.12));
    root.style.setProperty('--ui-card-dark',      shadeChannels(colors.primary_color, 0.18));
    root.style.setProperty('--ui-raised-dark',    shadeChannels(colors.primary_color, 0.26));
    root.style.setProperty('--ui-subtle-dark',    shadeChannels(colors.primary_color, 0.20));
    root.style.setProperty('--ui-input-dark',     shadeChannels(colors.primary_color, 0.20));
    root.style.setProperty('--ui-hover-dark',     shadeChannels(colors.primary_color, 0.30));
    root.style.setProperty('--ui-border-dark',    shadeChannels(colors.primary_color, 0.32));
    root.style.setProperty('--ui-border2-dark',   shadeChannels(colors.primary_color, 0.20));
    root.style.setProperty('--ui-border3-dark',   shadeChannels(colors.primary_color, 0.40));

    // Sidebar token — light: barely tinted white; dark: deep brand shade
    root.style.setProperty('--ui-sidebar-bg-light', tintChannels(colors.primary_color, 0.03));
    root.style.setProperty('--ui-sidebar-bg-dark',  shadeChannels(colors.primary_color, 0.22));
  }
  if (colors.accent_color) {
    root.style.setProperty('--color-brand-bright', hexToChannels(colors.accent_color));
  }
  if (colors.success_color) {
    root.style.setProperty('--color-success', hexToChannels(colors.success_color));
  }
}

/** Convert a #RRGGBB hex string into a space-separated "R G B" channel
 *  string for use in `rgb(var(--x) / <alpha-value>)` Tailwind colors. */
function hexToChannels(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r} ${g} ${b}`;
}

/** Mix `hex` over white at the given alpha and return solid "R G B" channels —
 *  used to derive a light brand tint from the primary color. */
function tintChannels(hex: string, alpha: number): string {
  const mix = (c: number) => Math.round(255 * (1 - alpha) + c * alpha);
  const r = mix(parseInt(hex.slice(1, 3), 16));
  const g = mix(parseInt(hex.slice(3, 5), 16));
  const b = mix(parseInt(hex.slice(5, 7), 16));
  return `${r} ${g} ${b}`;
}

/** Mix `hex` over black at the given alpha and return solid "R G B" channels —
 *  used to derive the very dark, brand-tinted chat surfaces from the primary color. */
function shadeChannels(hex: string, alpha: number): string {
  const shade = (c: number) => Math.round(c * alpha);
  const r = shade(parseInt(hex.slice(1, 3), 16));
  const g = shade(parseInt(hex.slice(3, 5), 16));
  const b = shade(parseInt(hex.slice(5, 7), 16));
  return `${r} ${g} ${b}`;
}
